import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ProfilePoint } from "../../../context/RulerContext";

Highcharts.setOptions({
  lang: {
    viewFullscreen: "Ver em tela cheia",
    exitFullscreen: "Sair da tela cheia",
    printChart: "Imprimir gráfico",
    downloadPNG: "Baixar imagem PNG",
    downloadJPEG: "Baixar imagem JPEG",
    downloadPDF: "Baixar documento PDF",
    downloadSVG: "Baixar imagem SVG",
    downloadCSV: "Baixar CSV",
    downloadXLS: "Baixar XLS",
    viewData: "Ver tabela de dados",
    hideData: "Ocultar tabela de dados",
    contextButtonTitle: "Menu do gráfico",
    noData: "Sem dados",
    resetZoom: "Resetar zoom",
    resetZoomTitle: "Resetar zoom",
    thousandsSep: ".",
    decimalPoint: ",",
    loading: "Carregando...",
  },
});

interface ProfileChartProps {
  data: ProfilePoint[];
  layerName: string;
  isLoading: boolean;
}

export default function ProfileChart({ data, layerName, isLoading }: ProfileChartProps) {
  if (isLoading) {
    return (
      <div className="h-48 flex flex-col items-center justify-center gap-2">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-xs text-gray-400">Calculando perfil...</span>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center">
        <span className="text-xs text-gray-400 text-center px-4">
          Nenhum dado disponível. Verifique se a camada raster está visível.
        </span>
      </div>
    );
  }

  const validValues = data.map(p => p.value).filter((v): v is number => v !== null);
  const minVal = validValues.length ? Math.min(...validValues) : 0;
  const maxVal = validValues.length ? Math.max(...validValues) : 0;
  const totalDist = data[data.length - 1]?.distance ?? 0;

  const options: Highcharts.Options = {
    chart: {
      type: "area",
      backgroundColor: "transparent",
      height: 180,
      margin: [12, 12, 52, 52],
      animation: { duration: 400 },
    },
    title: { text: undefined },
    xAxis: {
      min: 0,
      max: totalDist,
      title: {
        text: "Distância (km)",
        style: { fontSize: "10px", color: "#6b7280"},
        margin: 4,
      },
      labels: { style: { fontSize: "10px", color: "#6b7280" } },
      lineColor: "#e5e7eb",
      tickColor: "#e5e7eb",
    },
    yAxis: {
      min: minVal * 0.98,
      title: {
        text: layerName,
        style: { fontSize: "10px", color: "#6b7280" },
        margin: 6,
      },
      labels: { style: { fontSize: "10px", color: "#6b7280" } },
      gridLineColor: "#f3f4f6",
    },
    series: [
      {
        type: "area",
        name: layerName,
        data: data.map(p => [p.distance, p.value]),
        color: "#0C2856",
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(12,40,86,0.35)"],
            [1, "rgba(12,40,86,0.02)"],
          ],
        },
        lineWidth: 2,
        marker: { enabled: false, states: { hover: { enabled: true, radius: 3 } } },
        connectNulls: false,
      },
    ],
    tooltip: {
      backgroundColor: "rgba(12,40,86,0.92)",
      borderWidth: 0,
      borderRadius: 8,
      style: { color: "#fff", fontSize: "11px" },
      formatter() {
        const val = (this as any).y;
        const dist = (this as any).x;
        return `<b>${dist.toFixed(2)} km</b><br/>Valor: <b>${val !== null ? val.toFixed(2) : "—"}</b>`;
      },
    },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      area: { threshold: null as any },
    },
  };

  return (
    <div className="mx-3 my-2">
      <HighchartsReact highcharts={Highcharts} options={options} />
      <br />
    </div>
  );
}
